import { useEffect } from 'react';

import { baseApi } from '@/model/client';
import type { SseConnectionState } from '@/model/etlSource/api';
import type { EtlLiveEvent } from '@/model/etlSource/etlSource.model';
import { ETL_SOURCES_LIST_TAG, ETL_SOURCES_TAG } from '@/model/etlSource/tags';
import { logger } from '@/shared/helpers/logger';
import { useAppDispatch, useStateReader } from '@/shared/hooks/useStore';
import { subscribeEtlEvents } from '@/viewmodel/features/etlSource/subscribeEtlEvents';
import { selectApiToken } from '@/viewmodel/features/user';

import { useLiveUpdatesEnabled } from '@/viewmodel/features/etlSource/hooks/useLiveUpdatesEnabled';

const COALESCE_MS = 200;

export function useEtlLiveUpdates(projectId: string | undefined): void {
  useEtlLiveUpdatesFor(projectId ? [projectId] : []);
}

export function useEtlLiveUpdatesFor(projectIds: readonly string[]): void {
  const enabled = useLiveUpdatesEnabled();
  const dispatch = useAppDispatch();
  const readToken = useStateReader(selectApiToken);
  const key = [...new Set(projectIds.filter(Boolean))].sort().join(',');

  useEffect(() => {
    if (!enabled || !key) return;

    const pendingSources = new Set<string>();
    let flushTimer: ReturnType<typeof setTimeout> | undefined;

    const resync = (): void => {
      dispatch(baseApi.util.invalidateTags([ETL_SOURCES_TAG]));
    };

    const flush = (): void => {
      flushTimer = undefined;
      if (pendingSources.size === 0) return;

      const tags = [...pendingSources].map((id) => ({ type: ETL_SOURCES_TAG, id }));
      pendingSources.clear();
      dispatch(baseApi.util.invalidateTags([...tags, ETL_SOURCES_LIST_TAG]));
    };

    const onEvent = (event: EtlLiveEvent): void => {
      if (!event.sourceId) {
        resync();

        return;
      }

      pendingSources.add(event.sourceId);
      flushTimer ??= setTimeout(flush, COALESCE_MS);
    };

    const getToken = (): string | null => readToken() ?? null;

    const unsubscribes = key.split(',').map((projectId) =>
      subscribeEtlEvents(projectId, getToken, {
        onEvent,
        onResync: resync,
        onStateChange: (state: SseConnectionState): void => {
          logger.info('live updates connection state', {
            log_source: 'liveUpdates',
            state,
            project_id: projectId,
          });
        },
      }),
    );

    return (): void => {
      if (flushTimer) clearTimeout(flushTimer);
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [enabled, key, dispatch, readToken]);
}
